import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Renderer2,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  model,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Observable,
  Subject,
  catchError,
  debounceTime,
  forkJoin,
  of,
  switchMap,
  timer,
} from 'rxjs';
import { Dialog } from 'primeng/dialog';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { RadioButton } from 'primeng/radiobutton';
import { FloatLabel } from 'primeng/floatlabel';
import { Popover } from 'primeng/popover';
import { TableModule } from 'primeng/table';
import { AutoComplete } from 'primeng/autocomplete';
import { Select } from 'primeng/select';
import { Textarea } from 'primeng/textarea';
import { Tooltip } from 'primeng/tooltip';
import { AppraiserService } from '../../services/appraiser.service';
import { SearchService } from '../../services';
import { AuthService } from '../../services/auth.service';
import {
  GooglePlacesService,
  PlacePrediction,
  AddressFormData,
} from '../../services/google-places.service';
import { Appraiser, AppraiserQuickInfo, AppraiserSearchParams } from '../../models/appraiser.model';
import { CodeDescriptionPipe, PhoneFormatPipe } from '../../pipes';

export type AppraiserFilter = 'GRP/DTD' | 'Virtual' | 'Staff' | 'IA' | 'PTL';

/**
 * Mutually exclusive load states for the appraiser table. Keeping these in a single
 * signal (rather than separate `loading` / `error` flags) guarantees the table — and
 * therefore PrimeNG's "No appraiser found" empty message — can never render while the
 * request has failed.
 */
export type AppraiserLoadState = 'idle' | 'loading' | 'loaded' | 'error';

export interface DeclineReasonOption {
  label: string;
  value: string;
}
export interface BucketDeclineReason {
  reason: string;
  other: string;
}

export interface AppraiserRow {
  id: number;
  calendarEnabled: boolean;
  fullName: string;
  group: string;
  groupName: string;
  type: string;
  certified: string;
  towing: string;
  distance: string;
  distanceNumeric: number;
  availableDate: string;
  assignedToday: number;
  dailyCount: number;
  weeklyCount: number;
  dailyQuotaMet: boolean;
}

export interface GeocodedAddressData {
  formattedAddress: string;
  addressLine1: string;
  city: string;
  state: string;
  zipcode: string;
  county?: string;
  latitude?: number;
  longitude?: number;
}

export interface AppraiserSelectionEvent {
  appraiser: Appraiser;
  row: AppraiserRow;
  bucketDeclineReasons: Map<AppraiserFilter, BucketDeclineReason>;
  /** Populated only when the user modified the search address and it was geocoded. */
  addressData?: GeocodedAddressData;
}

/** Sequential buckets that require a decline reason before advancing. PTL is standalone. */
export const ORDERED_FILTERS: readonly AppraiserFilter[] = [
  'GRP/DTD',
  'Virtual',
  'Staff',
  'IA',
] as const;

@Component({
  selector: 'app-appraiser-select-dialog',
  imports: [
    FormsModule,
    AsyncPipe,
    NgTemplateOutlet,
    Dialog,
    Button,
    InputText,
    RadioButton,
    FloatLabel,
    TableModule,
    AutoComplete,
    CodeDescriptionPipe,
    PhoneFormatPipe,
    Popover,
    Select,
    Textarea,
    Tooltip,
  ],
  templateUrl: './appraiser-select-dialog.component.html',
  styleUrl: './appraiser-select-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppraiserSelectDialogComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly renderer = inject(Renderer2);
  private readonly appraiserService = inject(AppraiserService);
  private readonly searchService = inject(SearchService);
  private readonly googlePlacesService = inject(GooglePlacesService);
  private readonly authService = inject(AuthService);

  readonly appraiserPopover = viewChild<Popover>('appraiserPopover');
  readonly certifiedPopover = viewChild<Popover>('certifiedPopover');

  // ── Inputs ───────────────────────────────────────────────────────────────

  visible = model<boolean>(false);
  dialogHeader = input<string>('Select Appraiser');
  claimNumber = input<string>('');
  unitName = input<string>('');
  /** Current appraiser name to display in the metadata bar (e.g. for Transfer flow). */
  currentAppraiser = input<string>('–');
  /** Pre-computed inspection address; used as the initial value of the address search field. */
  initialAddress = input<string>('');
  /** AppraiserSearchParams built by the parent. Triggers appraiser load when dialog opens. */
  baseParams = input<AppraiserSearchParams | null>(null);
  /** Label for the primary action button. */
  submitLabel = input<string>('Continue');
  /** While true the submit button shows a spinner (e.g. during dispatch creation). */
  submitting = input<boolean>(false);
  /**
   * Pass your DECLINE_OPTIONS_BY_BUCKET record to enable the tab-decline flow.
   * When null (default) tab switching is free — no decline modal is shown.
   */
  declineOptions = input<Record<AppraiserFilter, DeclineReasonOption[]> | null>(null);

  // ── Outputs ──────────────────────────────────────────────────────────────

  appraiserSelected = output<AppraiserSelectionEvent>();
  /** Emitted only when the user explicitly cancels (button or ×). */
  cancelled = output<void>();

  // ── Internal state ───────────────────────────────────────────────────────

  appraisers = signal<AppraiserRow[]>([]);
  private rawAppraisers = signal<Appraiser[]>([]);
  private originalAppraisers = signal<AppraiserRow[]>([]);
  private originalRawAppraisers = signal<Appraiser[]>([]);

  loadState = signal<AppraiserLoadState>('idle');
  /** Populated only while `loadState()` is `'error'`. */
  errorMessage = signal('');

  selectedAppraiserId = signal<number | null>(null);
  activeFilter = signal<AppraiserFilter>('GRP/DTD');
  unlockedFilters = signal(new Set<AppraiserFilter>(['GRP/DTD', 'Virtual', 'PTL']));

  readonly appraiserFilters: { value: AppraiserFilter; label: string; noResultsLabel?: string }[] =
    [
      { value: 'GRP/DTD', label: 'GRP/DTD Shops' },
      { value: 'Virtual', label: 'Virtual', noResultsLabel: 'Virtual (Photo Inspection)' },
      { value: 'Staff', label: 'Staff' },
      { value: 'IA', label: 'IA', noResultsLabel: 'IA (Independent Adjuster)' },
      { value: 'PTL', label: 'PTL', noResultsLabel: 'PTL (Possible Total Loss)' },
    ];

  searchAddress = signal('');
  addressSuggestions = signal<string[]>([]);
  private readonly addressPredictions = signal<PlacePrediction[]>([]);
  private readonly addressSearch$ = new Subject<string>();

  appraiserNameSuggestions = signal<string[]>([]);
  private readonly nameSearch$ = new Subject<string>();
  searchAllSelectedValue = signal<string | null>(null);
  searchAllActive = signal(false);
  private lastGeocodedResult = signal<AddressFormData | null>(null);

  showColumnFilters = signal(false);
  columnFilters = signal<Partial<Record<keyof AppraiserRow, string>>>({});
  hideFullQuota = signal(true);
  mobileDetailsExpanded = signal(false);

  quickInfoAppraiser = signal<AppraiserQuickInfo | null>(null);
  certifiedInfo = signal('');

  /** Split the raw specialtyMake string into individual, sorted, de-duplicated makes. */
  certifiedMakes = computed(() => {
    const raw = this.certifiedInfo();
    if (!raw || raw === '–') return [];
    const items = raw
      .split(/[,;|\n\r/]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    return Array.from(new Set(items)).sort((a, b) => a.localeCompare(b));
  });

  // Decline flow state (used only when declineOptions input is set)
  declineModalVisible = signal(false);
  declineFromFilter = signal<AppraiserFilter>('GRP/DTD');
  pendingFilter = signal<AppraiserFilter | null>(null);
  declineReason = signal('');
  /** Auto-resets to '' whenever declineReason changes — Angular 20 linkedSignal. */
  declineReasonOther = linkedSignal({ source: this.declineReason, computation: () => '' });
  declineReasonSubmitted = signal(false);
  bucketDeclineReasons = signal<Map<AppraiserFilter, BucketDeclineReason>>(new Map());

  // ── Computed ─────────────────────────────────────────────────────────────

  appraiserFilterCounts = computed(() => {
    const all = this.originalAppraisers().length ? this.originalAppraisers() : this.appraisers();
    return new Map(
      this.appraiserFilters.map((f) => {
        const groupList = all.filter((a) => this.matchesFilter(a.group, f.value));
        // Mirror the quota filter so badge count matches visible rows
        const visible =
          this.hideFullQuota() && groupList.length > 1
            ? groupList.filter((a) => !a.dailyQuotaMet)
            : groupList;
        return [f.value, visible.length];
      }),
    );
  });

  filteredAppraisers = computed(() => {
    let list = this.appraisers();
    if (!this.searchAllActive()) {
      list = list.filter((a) => this.matchesFilter(a.group, this.activeFilter()));
      // Legacy rule: "dailyQuotaMet eq 1 AND count ne 1"
      // Hide quota-met appraisers only when there is more than one record in the
      // current group — never leave the user with an empty list.
      if (this.hideFullQuota() && list.length > 1) {
        list = list.filter((a) => !a.dailyQuotaMet);
      }
      // Sort userDistance, intervalCountToday, intervalCountYearMonthDay, intervalCountYearWeek, fullName
      list = [...list].sort(
        (a, b) =>
          a.distanceNumeric - b.distanceNumeric ||
          a.assignedToday - b.assignedToday ||
          a.dailyCount - b.dailyCount ||
          a.weeklyCount - b.weeklyCount ||
          a.fullName.localeCompare(b.fullName),
      );
    }
    for (const [key, val] of Object.entries(this.columnFilters()) as [
      keyof AppraiserRow,
      string,
    ][]) {
      const q = val.trim().toLowerCase();
      if (q)
        list = list.filter((a) =>
          String(a[key] ?? '')
            .toLowerCase()
            .includes(q),
        );
    }
    return list;
  });

  noAppraisersMessage = computed(() => {
    if (this.searchAllActive())
      return `No appraiser found matching "${this.searchAllSelectedValue() ?? ''}"`;
    const filter = this.appraiserFilters.find((f) => f.value === this.activeFilter());
    return `No appraiser found for ${filter?.noResultsLabel ?? filter?.value ?? this.activeFilter()}.`;
  });

  canContinue = computed(() => this.selectedAppraiserId() !== null && !this.submitting());

  /** Distance, Towing, Certified are only relevant for GRP/DTD appraisers. */
  showGrpColumns = computed(() => this.activeFilter() === 'GRP/DTD' && !this.searchAllActive());

  /** Dropdown options for mobile bucket filter. */
  mobileBucketOptions = computed(() =>
    this.appraiserFilters.map((f) => ({
      label: `${f.label} (${this.appraiserFilterCounts().get(f.value) ?? 0})`,
      value: f.value,
      disabled:
        !this.unlockedFilters().has(f.value) ||
        (this.appraiserFilterCounts().get(f.value) ?? 0) === 0 ||
        ((f.value === 'Staff' || f.value === 'IA') && this.addressHasUnknowns()),
    })),
  );

  addressModified = computed(() => this.searchAddress().trim() !== this.initialAddress().trim());

  /** IA / Staff require a fully known address — disable when address contains unknowns. */
  addressHasUnknowns = computed(() => {
    const address = this.initialAddress().toUpperCase();
    if (address.includes('????') || address.includes('UNK')) return true;
    const zip = this.baseParams()?.zipCd ?? '';
    return zip === '9999' || zip === '99999';
  });

  /** Show "Search All" based on the logged-in user's own searchAllVisibilityInd from profile API. */
  readonly showSearchAll = computed(
    () => this.authService.userProfile()?.searchAllVisibilityInd === 'Y',
  );

  declineReasonOptions = computed(
    () => (this.declineOptions() ?? {})[this.declineFromFilter()] ?? [],
  );
  declineModalTitle = computed(() => `Claimant Declines ${this.declineFromFilter()} Appraisal`);
  declineContinueLabel = computed(() => {
    const pending = this.pendingFilter();
    return pending ? `Continue to ${pending} Appraiser Options` : 'Continue';
  });
  canContinueDeclineModal = computed(() => {
    const reason = this.declineReason();
    if (!reason) return false;
    if (reason !== 'OTHER') return true;
    return this.declineReasonOther().trim().length > 0;
  });

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  constructor() {
    // Reset and load appraisers whenever the dialog opens
    effect(() => {
      if (this.visible()) {
        untracked(() => {
          this.resetState();
          this.searchAddress.set(this.initialAddress());
          const params = this.baseParams();
          if (params) this.loadInitialAppraisers(params);
        });
      }
    });

    this.nameSearch$
      .pipe(
        debounceTime(300),
        switchMap((q) =>
          this.searchService.getAppraiserNameOptions(q).pipe(catchError(() => of([]))),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((opts) => this.appraiserNameSuggestions.set(opts.map((o) => o.label)));

    this.addressSearch$
      .pipe(
        debounceTime(300),
        switchMap((q) =>
          this.googlePlacesService.getPlacePredictions(q).pipe(catchError(() => of([]))),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((predictions) => {
        this.addressPredictions.set(predictions);
        this.addressSuggestions.set(predictions.map((p) => p.description));
      });
  }

  // ── Public handlers ───────────────────────────────────────────────────────

  onAddressSearch(event: { query: string }): void {
    this.addressSearch$.next(event.query ?? '');
  }

  onRefreshAddress(): void {
    this.searchAddress.set(this.initialAddress());
    const params = this.baseParams();
    if (params) this.loadInitialAppraisers(params);
  }

  onAddressSelect(event: { value: string }): void {
    const prediction = this.addressPredictions().find((p) => p.description === event.value);
    this.searchAddress.set(event.value);
    const geocodeCall = prediction
      ? this.googlePlacesService.getPlaceDetails(prediction.place_id)
      : this.googlePlacesService.geocodeAddress(event.value);
    this.geocodeAndReload(geocodeCall);
  }

  onAppraiserNameSearch(event: { query: string }): void {
    this.nameSearch$.next(event.query ?? '');
  }

  onAppraiserNameSelect(event: { value: string }): void {
    const name = event?.value?.trim();
    if (!name) return;
    this.searchAllSelectedValue.set(name);
    this.appraisers.set([]);
    this.rawAppraisers.set([]);
    this.searchAllActive.set(true);
    this.loadByName(name);
  }

  /** Reset native input scroll so trailing ellipsis is visible after the field is no longer focused. */
  onSearchAllBlur(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (input) this.renderer.setProperty(input, 'scrollLeft', 0);
  }

  onSearchAllClear(): void {
    this.searchAllSelectedValue.set(null);
    this.searchAllActive.set(false);
    this.appraisers.set(this.originalAppraisers());
    this.rawAppraisers.set(this.originalRawAppraisers());
  }

  selectFilter(filter: AppraiserFilter): void {
    if (filter === this.activeFilter() && !this.searchAllActive()) return;
    if (this.searchAllActive()) this.onSearchAllClear();

    if (this.declineOptions()) {
      const skipped = this.getSkippedBuckets(this.activeFilter(), filter);
      const firstUnrecorded = skipped.find((b) => !this.bucketDeclineReasons().has(b));
      if (firstUnrecorded) {
        if ((this.appraiserFilterCounts().get(firstUnrecorded) ?? 0) === 0) {
          this.autoDeclineBucket(firstUnrecorded);
          this.activeFilter.set(firstUnrecorded);
          this.selectFilter(filter);
          return;
        }
        this.openDeclineModal(firstUnrecorded, filter);
        return;
      }
    }

    this.activeFilter.set(filter);
    this.unlockNextFilter(filter);
  }

  toggleColumnFilters(): void {
    const next = !this.showColumnFilters();
    this.showColumnFilters.set(next);
    if (!next) this.columnFilters.set({});
  }

  updateColumnFilter(key: keyof AppraiserRow, value: string): void {
    this.columnFilters.update((f) => ({ ...f, [key]: value }));
  }

  showCertifiedInfo(event: MouseEvent, certified: string): void {
    event.stopPropagation();
    this.appraiserPopover()?.hide();

    const popover = this.certifiedPopover();
    if (!popover) return;

    this.certifiedInfo.set(certified);
    this.showOrRealign(popover, event);
  }

  /**
   * PrimeNG flips popovers against the viewport but not against a parent dialog.
   * Re-position above the trigger when the popover would spill past the dialog's bottom.
   */
  onPopoverShow(popover: Popover): void {
    const trigger = popover.target as HTMLElement | null;
    const popoverEl = popover.container;
    const dialog = trigger?.closest<HTMLElement>('.p-dialog-content');
    if (!trigger || !popoverEl || !dialog) return;
    const triggerRect = trigger.getBoundingClientRect();
    const popoverRect = popoverEl.getBoundingClientRect();
    const dialogRect = dialog.getBoundingClientRect();
    if (popoverRect.bottom <= dialogRect.bottom - 8) return;
    const flipTop = window.scrollY + triggerRect.top - popoverRect.height - 8;
    const minTop = window.scrollY + dialogRect.top + 8;
    this.renderer.setStyle(popoverEl, 'top', `${Math.max(flipTop, minTop)}px`);
  }

  showAppraiserInfo(event: MouseEvent, row: AppraiserRow): void {
    event.stopPropagation();
    this.certifiedPopover()?.hide();

    const raw = this.rawAppraisers().find((a) => a.userId === row.id);
    if (!raw) return;

    const partyTypeCd = raw.partyTypeCd ?? raw.parties?.[0]?.partyTypeCd ?? '';

    const popover = this.appraiserPopover();
    if (!popover) return;

    this.quickInfoAppraiser.set({
      name: raw.fullName,
      adjusterId: raw.streamUserId ?? '',
      adjusterIdentifier: row.groupName,
      adjusterType: partyTypeCd.toUpperCase(),
      emailAddress: '',
      phoneNumber: '',
      businessPhone: null,
      businessExtension: null,
      cellPhone: null,
      textAddress: '',
      addressCounty: null,
      address: '',
      coordinatorName: raw.coordinatorName ?? null,
    });

    this.showOrRealign(popover, event);

    if (raw.streamUserId && raw.streamRegionCd) {
      this.appraiserService
        .getAppraiserContacts(raw.streamUserId, partyTypeCd, raw.streamRegionCd)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((contacts) => {
          const current = this.quickInfoAppraiser();
          if (current) this.quickInfoAppraiser.set({ ...current, ...contacts });
        });
    }
  }

  copied = signal(false);

  copyAppraiserInfo(info: AppraiserQuickInfo): void {
    const lines: string[] = [
      info.name,
      info.address ? `Address: ${info.address}` : '',
      info.addressCounty ? `Address County: ${info.addressCounty}` : '',
      info.cellPhone ? `Cell Phone: ${info.cellPhone}` : '',
      info.businessPhone ? `Business Phone: ${info.businessPhone}` : '',
      info.businessExtension ? `Business Extension: ${info.businessExtension}` : '',
      info.phoneNumber ? `Phone: ${info.phoneNumber}` : '',
    ].filter(Boolean);
    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      this.copied.set(true);
      timer(2000)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.copied.set(false));
    });
  }

  onContinue(): void {
    if (!this.canContinue()) return;
    this.emitSelection();
  }

  private emitSelection(): void {
    const raw = this.rawAppraisers().find((a) => a.userId === this.selectedAppraiserId());
    const row =
      this.filteredAppraisers().find((a) => a.id === this.selectedAppraiserId()) ??
      this.appraisers().find((a) => a.id === this.selectedAppraiserId());
    if (!raw || !row) return;
    const event: AppraiserSelectionEvent = {
      appraiser: raw,
      row,
      bucketDeclineReasons: this.getDeclineReasonsSkippedBefore(this.activeFilter()),
    };
    if (this.addressModified()) {
      const geocoded = this.lastGeocodedResult();
      if (geocoded) {
        event.addressData = {
          formattedAddress: this.searchAddress(),
          addressLine1: geocoded.addressLine1,
          city: geocoded.city,
          state: geocoded.state,
          zipcode: geocoded.zipcode,
          county: geocoded.county,
          latitude: geocoded.latitude,
          longitude: geocoded.longitude,
        };
      }
    }
    this.appraiserSelected.emit(event);
  }

  onCancel(): void {
    this.visible.set(false);
    this.cancelled.emit();
  }

  onHide(): void {
    this.resetState();
  }

  // Decline flow handlers

  onDeclineModalCancel(): void {
    this.declineModalVisible.set(false);
    this.pendingFilter.set(null);
    this.declineReason.set('');
    this.declineReasonSubmitted.set(false);
  }

  onDeclineModalContinue(): void {
    this.declineReasonSubmitted.set(true);
    if (!this.canContinueDeclineModal()) return;
    const declined = this.declineFromFilter();
    this.bucketDeclineReasons.update((m) => {
      const next = new Map(m);
      next.set(declined, { reason: this.declineReason(), other: this.declineReasonOther() });
      return next;
    });
    const targetFilter = this.pendingFilter();
    this.onDeclineModalCancel();
    if (targetFilter) this.selectFilter(targetFilter);
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  /** Show the popover, or realign it in-place if already open (avoids animation-cycle bugs). */
  private showOrRealign(popover: Popover, event: MouseEvent): void {
    if (popover.overlayVisible) {
      popover.target = event.currentTarget ?? event.target;
      if (popover.container) {
        this.renderer.removeClass(popover.container, 'p-popover-flipped');
        this.renderer.removeAttribute(popover.container, 'data-p-popover-flipped');
      }
      popover.align();
      this.onPopoverShow(popover);
    } else {
      popover.show(event);
    }
  }

  private geocodeAndReload(call: Observable<AddressFormData | null>): void {
    this.loadState.set('loading');
    this.errorMessage.set('');
    call
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((geocoded) => this.handleGeocodeResult(geocoded));
  }

  /**
   * Initial load: use parent coordinates if present (Transfer); otherwise geocode
   * the inspection address first so the spinner is distance-sorted (Create Dispatch).
   */
  private loadInitialAppraisers(params: AppraiserSearchParams): void {
    const hasCoords = !!(params.loclat && params.loclng);
    if (hasCoords || !this.initialAddress().trim()) {
      this.loadAppraisers(params, true);
      return;
    }
    this.geocodeAndReload(this.googlePlacesService.geocodeAddress(this.initialAddress()));
  }

  private handleGeocodeResult(geocoded: AddressFormData | null): void {
    if (!geocoded) {
      // Geocode failed (Google API rate-limit, no result, script not loaded, etc.).
      // Fall back to the base params (unit.inspState/inspZip/inspCounty) so the
      // spinner still fires — otherwise the dialog is stuck empty with no
      // dispatch/spinner call made at all.
      const params = this.baseParams();
      if (!params) {
        this.failLoad('Unable to search for appraisers — missing claim or unit details.');
        return;
      }
      this.loadAppraisers(params, true);
      return;
    }
    const params = this.baseParams();
    if (!params) {
      this.failLoad('Unable to search for appraisers — missing claim or unit details.');
      return;
    }
    this.lastGeocodedResult.set(geocoded);
    this.loadAppraisers(
      {
        ...params,
        stateCd: geocoded.state,
        zipCd: geocoded.zipcode,
        countyCd: geocoded.county,
        loclat: geocoded.latitude != null ? String(geocoded.latitude) : undefined,
        loclng: geocoded.longitude != null ? String(geocoded.longitude) : undefined,
      },
      true,
    );
  }

  private loadAppraisers(params: AppraiserSearchParams, saveAsOriginal = false): void {
    this.loadState.set('loading');
    this.errorMessage.set('');
    forkJoin({
      results: this.appraiserService.getAppraisers(params),
      // Group-code labels are a display nicety — don't fail the whole search over them.
      userGroupMap: this.searchService
        .getUserGroupCodes()
        .pipe(catchError(() => of(new Map<string, string>()))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ results, userGroupMap }) => {
          // Settle the state before the auto-advance cascade below, so any nested
          // load it triggers is the last writer.
          this.loadState.set('loaded');
          const rows = this.mapRows(results, userGroupMap);
          this.rawAppraisers.set(results);
          this.appraisers.set(rows);
          if (saveAsOriginal) {
            this.originalAppraisers.set(rows);
            this.originalRawAppraisers.set(results);
            // Auto-advance past all empty leading buckets to land on the first one with appraisers.
            // selectFilter cascade handles auto-declining every empty bucket along the way.
            const firstNonEmpty = ORDERED_FILTERS.find(
              (f) => rows.filter((a) => this.matchesFilter(a.group, f)).length > 0,
            );
            if (firstNonEmpty && firstNonEmpty !== 'GRP/DTD') {
              this.selectFilter(firstNonEmpty);
            }
            // A disabled (zero-count) tab can never be clicked, so unlockNextFilter()
            // would never be called for it. Pre-unlock the following bucket directly.
            for (const bucket of ['Virtual', 'Staff'] as AppraiserFilter[]) {
              if (rows.filter((a) => this.matchesFilter(a.group, bucket)).length === 0) {
                this.unlockNextFilter(bucket);
              }
            }
          }
        },
        error: () => this.failLoad('Failed to load appraisers. Please try again.'),
      });
  }

  /**
   * Enters the terminal error state. The table is not rendered in this state, so the
   * user never sees "No appraiser found" for a search that never actually completed.
   */
  private failLoad(message: string): void {
    this.errorMessage.set(message);
    this.loadState.set('error');
  }

  private loadByName(fullName: string): void {
    const base = this.baseParams();
    // Reuse refdate already computed by the parent (transferBaseParams) so
    // name-search and regular-search are consistent with each other.
    const refdate = base?.refdate ?? this.appraiserService.calculateReferenceDate();
    this.loadAppraisers({
      companyCd: base?.companyCd ?? '',
      lobCd: '',
      stateCd: '',
      zipCd: '',
      coverageCd: '',
      refdate,
      fullName,
      loclat: base?.loclat,
      loclng: base?.loclng,
    });
  }

  private mapRows(results: Appraiser[], userGroupMap: Map<string, string>): AppraiserRow[] {
    return results.map((a) => {
      // An appraiser can belong to multiple groups (e.g. "GRP,CERT").
      // Join ALL userGroupCd values from the usergroups array.
      const allGroups =
        a.usergroups?.map((ug) => ug.userGroupCd).filter((g): g is string => !!g) ?? [];
      const userGroup = a.userGroupCd ?? allGroups[0] ?? '';
      const allGroupsJoined = allGroups.length > 0 ? allGroups.join(',') : userGroup;

      const partyType = a.partyTypeCd ?? a.parties?.[0]?.partyTypeCd ?? '';
      const distanceValue = a.userDistance ?? a.distance ?? '';
      const distanceNumeric = distanceValue ? parseFloat(String(distanceValue)) : Infinity;
      return {
        id: a.userId,
        calendarEnabled: a.assignTypeCd === 'CALENDAR',
        fullName: a.fullName,
        group: allGroupsJoined || '–',
        groupName: allGroups.map((g) => userGroupMap.get(g) || g).join(', ') || userGroup || '–',
        type: partyType || a.assignTypeCd || '–',
        certified: a.specialtyMake || a.specialtyCd || '–',
        towing: a.towingSvcsOfferedInd ?? '–',
        distance: distanceValue ? `${distanceValue} mi` : '–',
        distanceNumeric,
        availableDate: a.referenceDate ?? '–',
        assignedToday: a.intervalCountToday ?? 0,
        dailyCount: a.intervalCountYearMonthDay ?? 0,
        weeklyCount: a.intervalCountYearWeek ?? 0,
        dailyQuotaMet: Number(a.dailyQuotaMet) === 1,
      };
    });
  }

  private matchesFilter(group: string, filter: AppraiserFilter): boolean {
    // group may contain multiple comma-separated codes (e.g. "GRP,CERT")
    // so we check each segment individually — matching legacy behaviour where
    // all userGroupCd values are joined and the full string is evaluated.
    const segments = group
      .toLowerCase()
      .split(',')
      .map((s) => s.trim());
    const isVirtual = segments.some((s) => s.includes('photo'));
    const isIa = segments.some((s) => s === 'ia');
    const isGrpDtd = segments.some((s) => s.includes('grp') || s.includes('dtd'));
    const isPtl = segments.some((s) => s.includes('svg'));
    switch (filter) {
      case 'Virtual':
        return isVirtual;
      case 'IA':
        return isIa;
      case 'PTL':
        return isPtl;
      case 'Staff':
        return !isVirtual && !isIa && !isGrpDtd && !isPtl;
      default:
        return isGrpDtd;
    }
  }

  private unlockNextFilter(filter: AppraiserFilter): void {
    const next: Partial<Record<AppraiserFilter, AppraiserFilter>> = {
      Virtual: 'Staff',
      Staff: 'IA',
    };
    const toUnlock = next[filter];
    if (toUnlock) this.unlockedFilters.update((s) => new Set([...s, toUnlock]));
  }

  private getSkippedBuckets(from: AppraiserFilter, to: AppraiserFilter): AppraiserFilter[] {
    const fromIdx = ORDERED_FILTERS.indexOf(from);
    const toIdx = ORDERED_FILTERS.indexOf(to);
    if (toIdx <= fromIdx) return [];
    return ORDERED_FILTERS.slice(fromIdx, toIdx) as AppraiserFilter[];
  }

  /**
   * Only buckets earlier in the hierarchy than the one finally booked were truly skipped.
   * Backward navigation (e.g. declining Virtual then booking GRP/DTD) leaves stale entries
   * in bucketDeclineReasons for buckets at or after the final pick — drop those.
   */
  private getDeclineReasonsSkippedBefore(
    finalBucket: AppraiserFilter,
  ): Map<AppraiserFilter, BucketDeclineReason> {
    const finalIdx = ORDERED_FILTERS.indexOf(finalBucket);
    const result = new Map<AppraiserFilter, BucketDeclineReason>();
    for (const [bucket, reason] of this.bucketDeclineReasons()) {
      if (ORDERED_FILTERS.indexOf(bucket) < finalIdx) result.set(bucket, reason);
    }
    return result;
  }

  private autoDeclineBucket(bucket: AppraiserFilter): void {
    this.bucketDeclineReasons.update((m) => {
      const next = new Map(m);
      next.set(bucket, { reason: 'NO_APPRAISERS_AVAILABLE', other: '' });
      return next;
    });
  }

  private openDeclineModal(forBucket: AppraiserFilter, targetFilter: AppraiserFilter): void {
    this.declineFromFilter.set(forBucket);
    this.pendingFilter.set(targetFilter);
    this.declineReason.set('');
    this.declineReasonSubmitted.set(false);
    this.declineModalVisible.set(true);
  }

  private resetState(): void {
    this.appraisers.set([]);
    this.rawAppraisers.set([]);
    this.originalAppraisers.set([]);
    this.originalRawAppraisers.set([]);
    this.selectedAppraiserId.set(null);
    this.activeFilter.set('GRP/DTD');
    this.unlockedFilters.set(new Set(['GRP/DTD', 'Virtual', 'PTL']));
    this.searchAllActive.set(false);
    this.searchAllSelectedValue.set(null);
    this.appraiserNameSuggestions.set([]);
    this.addressSuggestions.set([]);
    this.addressPredictions.set([]);
    this.showColumnFilters.set(false);
    this.columnFilters.set({});
    this.hideFullQuota.set(true);
    this.mobileDetailsExpanded.set(false);
    this.loadState.set('idle');
    this.errorMessage.set('');
    this.quickInfoAppraiser.set(null);
    this.certifiedInfo.set('');
    this.bucketDeclineReasons.set(new Map());
    this.declineModalVisible.set(false);
    this.declineFromFilter.set('GRP/DTD');
    this.pendingFilter.set(null);
    this.declineReason.set('');
    this.declineReasonOther.set('');
    this.declineReasonSubmitted.set(false);
  }
}
